import { formatScore } from './scoreboard';
import type { ScoreLinkBoard, ScoreLinkBoardMetric } from './scoreboardApi';

/**
 * The scoreboard as an Ads entry link sees it: numbers only, read only.
 *
 * Deliberately has no buttons, no links, no tabs and no Analyze. Each section
 * is a native disclosure so the long board does not push the entry form off a
 * phone screen; the section this link enters numbers for starts open.
 */
export function ScoreEntryBoard({
  board,
  openSection,
}: {
  board: ScoreLinkBoard;
  openSection?: string | undefined;
}) {
  return (
    <section className="score-entry-board" aria-labelledby="score-entry-board-title">
      <h2 id="score-entry-board-title">The scoreboard</h2>
      <p className="score-entry-board-note">
        Read only. Numbers for {board.entry_date}, the 7 days to that date, and the month so far.
      </p>
      {board.sections.length === 0 ? <p>No sections to show yet.</p> : null}
      {board.sections.map((section, index) => (
        <details
          key={`${index}-${section.name}`}
          className="score-entry-board-section"
          open={section.name === openSection}
        >
          <summary>{section.name}</summary>
          {section.metrics.length === 0 ? (
            <p>No fields in this section.</p>
          ) : (
            <div className="score-entry-board-scroll">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Field</th>
                    <th scope="col">Day</th>
                    {section.kind === 'daily' ? (
                      <>
                        <th scope="col">7 days</th>
                        <th scope="col">Month</th>
                      </>
                    ) : null}
                    <th scope="col">Target</th>
                  </tr>
                </thead>
                <tbody>
                  {section.metrics.map((metric, row) => (
                    <tr key={`${row}-${metric.label}`}>
                      <th scope="row">{metric.label}</th>
                      <Cell value={metric.day} metric={metric} />
                      {section.kind === 'daily' ? (
                        <>
                          <Cell value={metric.week} metric={metric} />
                          <Cell value={metric.month} metric={metric} />
                        </>
                      ) : null}
                      <Cell value={metric.target} metric={metric} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </details>
      ))}
    </section>
  );
}

function Cell({ value, metric }: { value: number | null; metric: ScoreLinkBoardMetric }) {
  if (value === null)
    return (
      <td className="score-entry-board-empty">
        <span aria-hidden="true">–</span>
        <span className="ui-visually-hidden">None</span>
      </td>
    );
  return <td>{formatScore(value, metric)}</td>;
}
